import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Upload, FileText, Check, X, RefreshCw, AlertCircle, Download } from 'lucide-react';
import api from '../utils/api';

const PriceUpdates = () => {
  const [uploadData, setUploadData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadData(e.target.result);
    };
    
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else if (file.type.includes('sheet') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      toast.error('Excel files need to be converted to CSV format first');
      return;
    } else {
      reader.readAsText(file);
    }
  };

  // Handle paste data
  const handlePasteData = (event) => {
    const pastedData = event.target.value;
    setUploadData(pastedData);
    setSelectedFile(null);
  };

  // Process uploaded data
  const processData = async () => {
    if (!uploadData.trim()) {
      toast.error('Please provide data to process');
      return;
    }

    console.log('Data to be sent:', uploadData.substring(0, 500) + '...');
    console.log('Data length:', uploadData.length);

    setIsLoading(true);
    try {
      const response = await api.post('/price-updates/compare', {
        data: uploadData,
        format: selectedFile?.type || 'text'
      });

      console.log('API Response:', response.data);

      if (response.data.success) {
        setComparisonResults(response.data.data);
        toast.success(`Found ${response.data.data.comparisons.length} matches and ${response.data.data.newProducts.length} new products`);
      }
    } catch (error) {
      console.error('Error processing data:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to process data');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle comparison status
  const toggleComparisonStatus = (index, status) => {
    setComparisonResults(prev => ({
      ...prev,
      comparisons: prev.comparisons.map((comp, i) => 
        i === index ? { ...comp, status } : comp
      )
    }));
  };

  // Apply updates
  const applyUpdates = async () => {
    if (!comparisonResults) return;

    const acceptedUpdates = comparisonResults.comparisons.filter(comp => comp.status === 'accepted');
    
    if (acceptedUpdates.length === 0) {
      toast.error('No updates selected for application');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/price-updates/apply', {
        updates: acceptedUpdates
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setComparisonResults(null);
        setUploadData('');
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Error applying updates:', error);
      toast.error(error.response?.data?.message || 'Failed to apply updates');
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk accept/reject for items with changes
  const bulkAcceptChanges = () => {
    setComparisonResults(prev => ({
      ...prev,
      comparisons: prev.comparisons.map(comp => 
        (comp.changes.netWeight || comp.changes.price) 
          ? { ...comp, status: 'accepted' } 
          : comp
      )
    }));
  };

  const bulkRejectChanges = () => {
    setComparisonResults(prev => ({
      ...prev,
      comparisons: prev.comparisons.map(comp => 
        (comp.changes.netWeight || comp.changes.price) 
          ? { ...comp, status: 'rejected' } 
          : comp
      )
    }));
  };

  // Separate comparisons by change status
  const getGroupedComparisons = () => {
    if (!comparisonResults) return { noChanges: [], withChanges: [] };
    
    const noChanges = comparisonResults.comparisons.filter(comp => 
      !comp.changes.netWeight && !comp.changes.price
    );
    
    const withChanges = comparisonResults.comparisons.filter(comp => 
      comp.changes.netWeight || comp.changes.price
    );
    
    return { noChanges, withChanges };
  };

  // Reset form
  const resetForm = () => {
    setUploadData('');
    setSelectedFile(null);
    setComparisonResults(null);
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 90) return 'text-green-600 bg-green-50';
    if (similarity >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  // Function to highlight differences between two names
  const highlightNameDifferences = (uploadedName, existingName) => {
    if (!uploadedName || !existingName) return { uploadedHighlighted: uploadedName, existingHighlighted: existingName };
    
    const uploaded = uploadedName.toLowerCase();
    const existing = existingName.toLowerCase();
    
    // Simple word-based difference highlighting
    const uploadedWords = uploaded.split(' ');
    const existingWords = existing.split(' ');
    
    const highlightUploaded = uploadedWords.map(word => {
      const found = existingWords.some(existingWord => 
        existingWord.includes(word) || word.includes(existingWord)
      );
      return found ? word : `<mark class="bg-red-200 px-1 rounded">${word}</mark>`;
    }).join(' ');
    
    const highlightExisting = existingWords.map(word => {
      const found = uploadedWords.some(uploadedWord => 
        uploadedWord.includes(word) || word.includes(uploadedWord)
      );
      return found ? word : `<mark class="bg-blue-200 px-1 rounded">${word}</mark>`;
    }).join(' ');
    
    return {
      uploadedHighlighted: highlightUploaded,
      existingHighlighted: highlightExisting
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Price Updates</h1>
        <p className="text-gray-600">
          Upload daily price update data to compare and update existing products. 
          Only net weight and prices will be updated based on name matching.
        </p>
      </div>

      {/* Upload Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Data</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload CSV File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <span className="text-sm text-gray-600">
                  {selectedFile ? selectedFile.name : 'Click to upload CSV file'}
                </span>
              </label>
            </div>
          </div>

          {/* Text Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Or Paste Data Directly
            </label>
            <textarea
              rows="8"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              placeholder="Paste your tab-separated data here..."
              value={uploadData}
              onChange={handlePasteData}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={resetForm}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>
          <button
            onClick={processData}
            disabled={isLoading || !uploadData.trim()}
            className="px-6 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4" />
                Process Data
              </>
            )}
          </button>
        </div>
      </div>

      {/* Comparison Results */}
      {comparisonResults && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Comparison Summary</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600">{comparisonResults.summary.totalUploaded}</div>
                <div className="text-sm text-blue-600">Total Uploaded</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600">{comparisonResults.summary.foundMatches}</div>
                <div className="text-sm text-green-600">Found Matches</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-orange-600">{comparisonResults.summary.newProducts}</div>
                <div className="text-sm text-orange-600">New Products</div>
              </div>
            </div>
          </div>

          {/* No Changes Section */}
          {comparisonResults.comparisons.length > 0 && getGroupedComparisons().noChanges.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                No Changes Required
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {getGroupedComparisons().noChanges.length}
                </span>
              </h2>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Values
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Similarity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getGroupedComparisons().noChanges.map((comparison, index) => (
                      <tr key={index} className="bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{comparison.existingProduct.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            Weight: {comparison.existingProduct.currentNetWeight}kg | 
                            Price: {comparison.existingProduct.currentPrice === 0 ? 'Not Available' : `${comparison.existingProduct.currentPrice} AED`}
                          </div>
                          <div className="text-xs text-gray-400">Country: {comparison.existingProduct.importedCountry}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSimilarityColor(comparison.similarity)}`}>
                            {comparison.similarity}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Up to date
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Products with Changes */}
          {comparisonResults.comparisons.length > 0 && getGroupedComparisons().withChanges.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Products with Changes
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {getGroupedComparisons().withChanges.length}
                  </span>
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={bulkAcceptChanges}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={bulkRejectChanges}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={applyUpdates}
                    disabled={isLoading || comparisonResults.comparisons.filter(c => c.status === 'accepted').length === 0}
                    className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Check className="h-3 w-3" />
                        Apply Selected
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current vs New Values
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Similarity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getGroupedComparisons().withChanges.map((comparison, originalIndex) => {
                      const index = comparisonResults.comparisons.findIndex(c => c === comparison);
                      return (
                        <tr key={index} className={comparison.status === 'accepted' ? 'bg-green-50' : comparison.status === 'rejected' ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4">
                            <div className="space-y-2">
                              <div className="text-sm font-medium text-gray-900">{comparison.existingProduct.name}</div>
                              {comparison.similarity < 100 && (
                                <div className="space-y-1">
                                  <div className="text-xs text-gray-500">Name Comparison:</div>
                                  <div className="text-xs">
                                    <div className="mb-1">
                                      <span className="font-medium text-blue-700">Uploaded: </span>
                                      <span 
                                        dangerouslySetInnerHTML={{ 
                                          __html: highlightNameDifferences(comparison.uploadedProduct.name, comparison.existingProduct.name).uploadedHighlighted 
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <span className="font-medium text-green-700">Existing: </span>
                                      <span 
                                        dangerouslySetInnerHTML={{ 
                                          __html: highlightNameDifferences(comparison.uploadedProduct.name, comparison.existingProduct.name).existingHighlighted 
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="text-xs text-gray-400">Country: {comparison.existingProduct.importedCountry}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              <div className="text-xs text-gray-600">
                                <span className="font-medium">Weight:</span> {comparison.existingProduct.currentNetWeight}kg 
                                {comparison.changes.netWeight && (
                                  <span className="text-blue-600"> → {comparison.uploadedProduct.netWeight}kg</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600">
                                <span className="font-medium">Price:</span> {comparison.existingProduct.currentPrice === 0 ? 'Not Available' : `${comparison.existingProduct.currentPrice} AED`}
                                {comparison.changes.price && (
                                  <span className="text-green-600"> → {comparison.uploadedProduct.price === 0 ? 'Not Available' : `${comparison.uploadedProduct.price} AED`}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSimilarityColor(comparison.similarity)}`}>
                              {comparison.similarity}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              {comparison.changes.netWeight && comparison.changes.price ? (
                                <div className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded flex items-center gap-1">
                                  <span className="bg-blue-200 text-blue-800 px-1 rounded">Weight</span>
                                  <span className="bg-green-200 text-green-800 px-1 rounded">Price</span>
                                </div>
                              ) : comparison.changes.netWeight ? (
                                <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  Weight Change
                                </div>
                              ) : comparison.changes.price ? (
                                <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                  Price Change
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => toggleComparisonStatus(index, 'accepted')}
                                className={`p-1 rounded ${comparison.status === 'accepted' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-green-100'} transition-colors`}
                                title="Accept"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => toggleComparisonStatus(index, 'rejected')}
                                className={`p-1 rounded ${comparison.status === 'rejected' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-red-100'} transition-colors`}
                                title="Reject"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* New Products */}
          {comparisonResults.newProducts.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                New Products Found
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {comparisonResults.newProducts.length}
                </span>
              </h2>
              
              <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-orange-400 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-orange-800">
                      Manual Review Required
                    </h3>
                    <p className="mt-1 text-sm text-orange-700">
                      These products don't match any existing products. You'll need to manually add them or check for spelling variations.
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Weight & Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {comparisonResults.newProducts.map((product, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {product.netWeight}kg | {product.price === 0 ? 'Not Available' : `${product.price} AED`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {product.importedCountry} | {product.unit} | {product.modeOfTransportation}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceUpdates;